/*
  Notify me (snippets/notify-me.liquid, snippets/notify-me-dialog.liquid).

  Product page: two triggers, one dialog per section. « Notify me when
  available » replaces the sold-out buy button and follows variant changes;
  « Your size sold out? » shows when the selected size is in stock but others
  are not.

  Product cards: a sold-out card's « Notify me » link opens the page's one
  shared dialog (sections/notify-me-dialog.liquid) with the card's notify key
  and title, and no sizes.

  Either way the form posts to a Brevo hosted form and reports success only
  when Brevo says so. The product page snippet and the shared section both load
  this file, so everything sits behind one guard.
*/
if (!customElements.get('notify-me')) {
  // The dialog half: one per modal-dialog, shared by every trigger that opens it.
  class NotifyMeDialog {
    static for(modal) {
      modal.notifyMeDialog ??= new NotifyMeDialog(modal);
      return modal.notifyMeDialog;
    }

    constructor(modal) {
      this.modal = modal;
      this.form = this.modal.querySelector('form');
      this.status = this.modal.querySelector('.notify-me__status');
      this.sizes = this.modal.querySelector('[data-notify-sizes]');
      this.title = this.modal.querySelector('.notify-me__title');
      this.caseInput = this.form.querySelector('[name="NOTIFY_CASE"]');
      this.productInput = this.form.querySelector('[name="NOTIFY_PRODUCT"]');
      this.variantInput = this.form.querySelector('[name="NOTIFY_VARIANT"]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    // The shared dialog has no product of its own: the card hands it one.
    setProduct(key, title) {
      this.productInput.value = key;
      this.title.textContent = title;
    }

    open(kind, opener) {
      this.caseInput.value = kind;
      this.modal.querySelectorAll('[data-notify-intro]').forEach((intro) => {
        intro.hidden = intro.dataset.notifyIntro !== kind;
      });
      if (this.sizes) this.sizes.hidden = kind !== 'restock';
      this.form.querySelectorAll('.notify-me__field, .notify-me__optin, .notify-me__submit').forEach((el) => {
        el.hidden = false;
      });
      this.status.textContent = '';
      this.status.removeAttribute('data-state');
      this.modal.show(opener);
      this.form.querySelector('[name="EMAIL"]').focus();
    }

    setStatus(state) {
      this.status.dataset.state = state;
      this.status.textContent = this.status.dataset[state.replace(/-(\w)/g, (_, c) => c.toUpperCase())];
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.caseInput.value === 'restock' && this.sizes) {
        const ticked = [...this.sizes.querySelectorAll('input:checked')].map((chip) => chip.value);
        if (!ticked.length) {
          this.setStatus('no-size');
          return;
        }
        this.variantInput.value = ticked.join(',');
      } else {
        this.variantInput.value = '';
      }

      const email = this.form.querySelector('[name="EMAIL"]');
      if (!email.checkValidity()) {
        this.setStatus('invalid');
        email.focus();
        return;
      }

      const submit = this.form.querySelector('[type="submit"]');
      submit.disabled = true;
      try {
        const newsletter = this.form.querySelector('[name="NEWSLETTER"]').checked;
        const url = new URL(newsletter ? this.form.dataset.newsletterAction : this.form.action);
        url.searchParams.set('isAjax', '1');
        const response = await fetch(url, { method: 'POST', body: new FormData(this.form) });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.success === false) throw new Error('brevo');
        this.setStatus('success');
        this.form.querySelectorAll('.notify-me__field, .notify-me__optin, .notify-me__submit').forEach((el) => {
          el.hidden = true;
        });
        if (this.sizes) this.sizes.hidden = true;
        // The focused submit button just hid: keep focus in the dialog so Escape still closes it.
        this.modal.querySelector('[role="dialog"]').focus();
      } catch (error) {
        this.setStatus('error');
      } finally {
        submit.disabled = false;
      }
    }
  }

  customElements.define(
    'notify-me',
    class NotifyMe extends HTMLElement {
      connectedCallback() {
        // modal-dialog moves itself to <body>, so it is found by id, not inside this element.
        this.dialog = NotifyMeDialog.for(document.getElementById(this.dataset.modalId));
        this.restockButton = this.querySelector('.notify-me__restock');
        this.sizeLink = this.querySelector('[data-notify-size-link]');

        this.querySelectorAll('[data-notify-open]').forEach((trigger) =>
          trigger.addEventListener('click', () => this.dialog.open(trigger.dataset.notifyOpen, trigger))
        );

        this.setSoldOut(this.dataset.soldOut === 'true');

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId) return;
            this.onVariantChange(data.variant);
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      }

      onVariantChange(variant) {
        this.setSoldOut(Boolean(variant) && !variant.available);
        // Tick the size the shopper is looking at, if it is one of the sold-out chips.
        this.dialog.sizes?.querySelectorAll('input').forEach((chip) => {
          chip.checked = Boolean(variant) && chip.dataset.variantId === String(variant.id);
        });
      }

      // The theme keeps the disabled « Coming soon » button; it hides while
      // « Notify me when available » stands in for it.
      setSoldOut(soldOut) {
        this.restockButton.hidden = !soldOut;
        this.sizeLink.hidden = soldOut || this.dataset.anySoldOut !== 'true';
        if (this.submitButton) this.submitButton.hidden = soldOut;
      }
    }
  );

  customElements.define(
    'notify-me-card',
    class NotifyMeCard extends HTMLElement {
      connectedCallback() {
        if (this.button) return;
        this.button = this.querySelector('[data-notify-card]');
        this.button.addEventListener('click', (event) => this.onClick(event));
      }

      onClick(event) {
        // The card is one big link (the title's ::after covers it): the click stays here.
        event.preventDefault();
        event.stopPropagation();
        const modal = document.getElementById('NotifyMe-shared');
        if (!modal) return;
        const dialog = NotifyMeDialog.for(modal);
        dialog.setProduct(this.button.dataset.notifyProduct, this.button.dataset.notifyTitle);
        dialog.open('restock', this.button);
      }
    }
  );
}
